import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard } from "lucide-react";
import { useNotification } from "../contexts/NotificationContext";

import { getApiUrl } from "../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};


export default function Subscription() {
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedTier, setSelectedTier] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  
  // Stripe Elements setup
  const stripe = useStripe();
  const elements = useElements();
  
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
  };

  useEffect(() => {
    fetchSubscriptionData();
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setIsLoadingPaymentMethods(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/subscriptions/payment-methods"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentMethods(response.data);
    } catch (err) {
      console.error("Error fetching payment methods:", err);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const fetchSubscriptionData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [tiersRes, subscriptionRes] = await Promise.all([
        axios.get(getApiUrl("/api/subscriptions/tiers"), {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(getApiUrl("/api/subscriptions/current"), {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setTiers(tiersRes.data);
      setCurrentSubscription(subscriptionRes.data.subscription);
    } catch (err) {
      console.error("Error fetching subscription data:", err);
      showError("Failed to load subscription information");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    if (isProcessing) return;
    
    setIsProcessing(true);

    try {
      // If user doesn't have a current subscription, redirect to Stripe Checkout
      if (!currentSubscription) {
        const token = localStorage.getItem("token");
        
        // Get user profile data for checkout
        let userProfile = { name: "User", email: "user@example.com" };
        try {
          const profileResponse = await axios.get(getApiUrl("/api/profile"), {
            headers: { Authorization: `Bearer ${token}` }
          });
          userProfile = {
            name: profileResponse.data.name || "User",
            email: profileResponse.data.email || "user@example.com"
          };
        } catch (profileErr) {
          console.warn("Could not fetch user profile, using defaults:", profileErr);
        }
        
        const response = await axios.post(
          getApiUrl("/api/auth/create-checkout-session"),
          {
            name: userProfile.name,
            email: userProfile.email,
            password: "dummy", // Not used in checkout flow
            tierId: tier.id
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.url) {
          // Redirect to Stripe Checkout
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL received");
        }
        return;
      }

      // Existing subscriber changing plans - use direct API approach
      if (!stripe) {
        throw new Error("Stripe is not configured. Please check your environment variables.");
      }

      // Check if user has payment methods
      if (paymentMethods.length === 0) {
        // No payment methods, create subscription without payment method
        const token = localStorage.getItem("token");
        const response = await axios.post(
          getApiUrl("/api/subscriptions/create"),
          {
            tierId: tier.id,
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        // Refresh subscription data
        await fetchSubscriptionData();
        setSelectedTier(null);
        
        // Show success message
        showSuccess("Subscription updated successfully!");
        return;
      }

      // User has payment methods, proceed with payment
      const token = localStorage.getItem("token");
      const response = await axios.post(
        getApiUrl("/api/subscriptions/create"),
        {
          tierId: tier.id,
          paymentMethodId: paymentMethods[0].id, // Use first available payment method
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.requiresAction) {
        // Handle 3D Secure authentication if required
        const { error: confirmError } = await stripe.confirmCardPayment(
          response.data.stripeSubscription.latest_invoice.payment_intent.client_secret
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Refresh subscription data
      await fetchSubscriptionData();
      setSelectedTier(null);
      
      // Show success message
      showSuccess("Subscription updated successfully!");
      
    } catch (err) {
      console.error("Error creating subscription:", err);
      showError(err.response?.data?.error || err.message || "Failed to update subscription");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription || isProcessing) return;
    
    if (!confirm("Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period.")) {
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        getApiUrl("/api/subscriptions/cancel"),
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await fetchSubscriptionData();
      showSuccess("Subscription will be canceled at the end of the current period.");
    } catch (err) {
      console.error("Error canceling subscription:", err);
      showError(err.response?.data?.error || "Failed to cancel subscription");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!stripe || !elements || isProcessing) return;
    
    setIsProcessing(true);

    try {
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: "Card Holder", // You might want to get this from user profile
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        return;
      }

      const token = localStorage.getItem("token");
      await axios.post(
        getApiUrl("/api/subscriptions/payment-methods"),
        { paymentMethodId: paymentMethod.id },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await fetchPaymentMethods();
      setShowPaymentForm(false);
      showSuccess("Payment method added successfully!");
    } catch (err) {
      console.error("Error adding payment method:", err);
      showError(err.response?.data?.error || "Failed to add payment method");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!confirm("Are you sure you want to remove this payment method?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${getApiUrl("/api/subscriptions/payment-methods")}/${paymentMethodId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await fetchPaymentMethods();
      showSuccess("Payment method removed successfully!");
    } catch (err) {
      console.error("Error removing payment method:", err);
      showError(err.response?.data?.error || "Failed to remove payment method");
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/subscriptions/payment-methods")}/${paymentMethodId}/default`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await fetchPaymentMethods();
      showSuccess("Default payment method updated successfully!");
    } catch (err) {
      console.error("Error setting default payment method:", err);
      showError(err.response?.data?.error || "Failed to set default payment method");
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandIcons = {
      visa: "💳",
      mastercard: "💳", 
      amex: "💳",
      discover: "💳",
      jcb: "💳",
      dinersclub: "💳",
      unionpay: "💳"
    };
    return brandIcons[brand?.toLowerCase()] || "💳";
  };

  const getCardBrandColor = (brand) => {
    const brandColors = {
      visa: "from-blue-600 to-blue-800",
      mastercard: "from-red-500 to-orange-500", 
      amex: "from-green-600 to-blue-600",
      discover: "from-orange-500 to-red-500",
      jcb: "from-red-600 to-blue-600",
      dinersclub: "from-gray-700 to-gray-900",
      unionpay: "from-blue-500 to-green-500"
    };
    return brandColors[brand?.toLowerCase()] || "from-gray-500 to-gray-700";
  };

  const getCardBrandName = (brand) => {
    const brandNames = {
      visa: "Visa",
      mastercard: "Mastercard",
      amex: "American Express",
      discover: "Discover",
      jcb: "JCB",
      dinersclub: "Diners Club",
      unionpay: "UnionPay"
    };
    return brandNames[brand?.toLowerCase()] || "Card";
  };



  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CreditCard className="mr-2 h-8 w-8 text-black-500" />
            Subscription Management
          </h1>
          <p className="text-gray-600 mt-2">Choose the plan that best fits your horse management needs</p>
        </div>



        {/* Current Subscription Status */}
        {currentSubscription && (
          <div className="p-6 border-b border-gray-200">
            <div className="bg-blue-50 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">Current Subscription</h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-800">
                    <span className="font-medium">{currentSubscription.display_name}</span> - ${currentSubscription.monthly_price}/month
                  </p>
                  <p className="text-sm text-blue-600">
                    Next billing: {formatDate(currentSubscription.current_period_end)}
                  </p>
                  {currentSubscription.cancel_at_period_end && (
                    <p className="text-sm text-orange-600 font-medium">
                      ⚠️ Subscription will be canceled at the end of the current period
                    </p>
                  )}
                </div>
                {!currentSubscription.cancel_at_period_end && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={isProcessing}
                    className="px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {isProcessing ? "Processing..." : "Cancel Subscription"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Methods</h2>
          
          {isLoadingPaymentMethods ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Existing Payment Methods */}
              {paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {paymentMethods.map((method, index) => (
                    <div 
                      key={method.id} 
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        method.is_default 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-7 bg-gradient-to-r ${getCardBrandColor(method.card.brand)} rounded flex items-center justify-center`}>
                          <span className="text-xs font-medium text-white">
                            {getCardBrandIcon(method.card.brand)}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-medium">
                              {getCardBrandName(method.card.brand)} •••• {method.card.last4}
                            </p>
                            {method.is_default && (
                              <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Expires {method.card.exp_month}/{method.card.exp_year}
                          </p>
                          {method.billing_details?.name && (
                            <p className="text-sm text-gray-500">
                              {method.billing_details.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {!method.is_default && (
                          <button
                            onClick={() => handleSetDefaultPaymentMethod(method.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-300 rounded hover:bg-blue-50"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          onClick={() => handleRemovePaymentMethod(method.id)}
                          className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No payment methods on file.</p>
              )}

              {/* Add New Payment Method */}
              {!showPaymentForm ? (
                <button
                  onClick={() => setShowPaymentForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Payment Method
                </button>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Add New Payment Method</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                      <div className="border border-gray-300 rounded-md p-3">
                        <CardNumberElement options={cardElementOptions} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                        <div className="border border-gray-300 rounded-md p-3">
                          <CardExpiryElement options={cardElementOptions} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CVC</label>
                        <div className="border border-gray-300 rounded-md p-3">
                          <CardCvcElement options={cardElementOptions} />
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={handleAddPaymentMethod}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isProcessing ? "Adding..." : "Add Card"}
                      </button>
                      <button
                        onClick={() => setShowPaymentForm(false)}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Subscription Tiers */}
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative rounded-lg border-2 p-3 sm:p-4 lg:p-6 ${
                  currentSubscription?.tier_id === tier.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300"
                }`}
              >
                {currentSubscription?.tier_id === tier.id && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-3 sm:mb-4">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{tier.display_name}</h3>
                  <div className="mt-1">
                    <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">${tier.monthly_price}</span>
                    <span className="text-gray-600 text-xs sm:text-sm lg:text-base">/month</span>
                  </div>
                  <p className="text-gray-600 mt-1 text-xs sm:text-sm lg:text-base">{tier.description}</p>
                </div>

                <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">Max Horses:</span>
                    <span className="font-medium">
                      {tier.max_horses === -1 ? "Unlimited" : tier.max_horses}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">Family Members:</span>
                    <span className="font-medium">
                      {tier.max_family_members === -1 ? "Unlimited" : tier.max_family_members}
                    </span>
                  </div>
                </div>


                  {/* Plan Details */}
                  <div className="mb-3 sm:mb-4">
                    <h4 className="font-medium text-gray-900 mb-1 sm:mb-2 text-xs sm:text-sm lg:text-base">Plan Details</h4>
                    <div>
                      {tier.name === 'horsemgmt' && (
                        <ul className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1">
                          <li>Up to 50 horses</li>
                          <li>Horse information</li>
                          <li>Feed and supplements management</li>
                          <li>Vaccination tracking</li>
                          <li>Hoof care management</li>
                          <li>Dental care tracking</li>
                          <li>Breeding management</li>
                          <li>Deworming tracking</li>
                          <li>Farrier contacts</li>
                          <li>Veterinarian contacts</li>
                          <li>Cost tracking</li>
                        </ul>
                      )}
                      {tier.name === 'showindividual' && (
                        <ul className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1">
                          <li>Up to 4 horses</li>
                          <li>Basic horse information</li>
                          <li>Feed and supplements management</li>
                          <li>Vaccination tracking</li>
                          <li>Hoof care management</li>
                          <li>Dental care tracking</li>
                          <li>Breeding management</li>
                          <li>Deworming tracking</li>
                          <li>Farrier contacts</li>
                          <li>Veterinarian contacts</li>
                          <li>Connect with other users</li>
                          <li>Show Entries, Results and Memberships</li>
                          <li>KCIRodeo Entries, Results and Memberships Import</li>
                          <li>KCIRodeo Live Results</li>
                          <li>Analytics</li>
                          <li>Data export</li>
                        </ul>
                      )}
                      {tier.name === 'showfamily' && (
                        <ul className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1">
                          <li>Up to 12 horses</li>
                          <li>Horse information</li>
                          <li>Feed and supplements management</li>
                          <li>Vaccination tracking</li>
                          <li>Hoof care management</li>
                          <li>Dental care tracking</li>
                          <li>Breeding management</li>
                          <li>Deworming tracking</li>
                          <li>Farrier contacts</li>
                          <li>Veterinarian contacts</li>
                          <li>Connect with other users</li>
                          <li>Show Entries, Results and Memberships</li>
                          <li>KCIRodeo Entries, Results and Memberships Import</li>
                          <li>KCIRodeo Live Results</li>                          
                          <li>Analytics</li>
                          <li>Data export</li>
                          <li>Family plan up to 4 additional members</li>
                        </ul>
                      )}
                      {tier.name === 'showpremium' && (
                        <ul className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1">
                          <li>Up to 20 horses</li>
                          <li>Horse information</li>
                          <li>Feed and supplements management</li>
                          <li>Vaccination tracking</li>
                          <li>Hoof care management</li>
                          <li>Dental care tracking</li>
                          <li>Breeding management</li>
                          <li>Deworming tracking</li>
                          <li>Farrier contacts</li>
                          <li>Veterinarian contacts</li>
                          <li>Connect with other users</li>
                          <li>Manual Entries, Results and Memberships</li>
                          <li>KCIRodeo Entries, Results and Memberships Import</li>
                          <li>KCIRodeo Live Results</li>
                          <li>Advanced Analytics</li>
                          <li>Data export</li>
                          <li>Family plan up to 6 additional members</li>
                          <li>Media - Capture your memories with Photos and Videos</li>
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Button at bottom of panel */}
                  <button
                    onClick={() => handleSubscribe(tier)}
                    disabled={isProcessing || currentSubscription?.tier_id === tier.id}
                    className={`w-full py-2 sm:py-3 px-2 sm:px-3 lg:px-4 rounded-md font-medium transition-colors mt-auto text-xs sm:text-sm lg:text-base ${
                      currentSubscription?.tier_id === tier.id
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : currentSubscription && parseFloat(tier.monthly_price) > parseFloat(currentSubscription.monthly_price)
                        ? "bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        : currentSubscription && parseFloat(tier.monthly_price) < parseFloat(currentSubscription.monthly_price)
                        ? "bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                        : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    }`}
                  >
                    {isProcessing
                      ? "Processing..."
                      : currentSubscription?.tier_id === tier.id
                      ? "Current Plan"
                      : currentSubscription && parseFloat(tier.monthly_price) > parseFloat(currentSubscription.monthly_price)
                      ? `Upgrade to ${tier.display_name}`
                      : currentSubscription && parseFloat(tier.monthly_price) < parseFloat(currentSubscription.monthly_price)
                      ? `Downgrade to ${tier.display_name}`
                      : currentSubscription
                      ? "Switch to This Plan"
                      : "Choose Plan"}
                  </button>
              </div>
            ))}
          </div>
        </div>

        
      </div>
    </div>
  );
} 